import type {
  ElevatedAccessToken,
  ParsedElevatedAccessToken,
} from '../elevatedAccessTokens.types';
import { normalizeScopes } from '../normalizeScopes';

/**
 * Upserts an elevated access token into the current tokens array.
 *
 * Policy: Only one token per normalized scope-set is allowed.
 * If a token with the same normalized scopes exists, it is replaced.
 * This ensures we don't accumulate multiple tokens for the same scope combination.
 *
 * @param currentTokens - Current array of elevated access tokens
 * @param newToken - New token to upsert
 * @returns Updated array of tokens with the new token upserted
  * @notInstrumented
 */
export const upsertElevatedAccessToken = ({
  currentTokens,
  newToken,
}: {
  currentTokens: ElevatedAccessToken[];
  newToken: ParsedElevatedAccessToken;
}): ElevatedAccessToken[] => {
  const newScopeKey = normalizeScopes(newToken.scopes).join(' ');

  // Remove any existing token with the same normalized scopes
  const filteredTokens = currentTokens.filter((existingToken) => {
    const existingScopeKey = normalizeScopes(existingToken.scopes).join(' ');
    return existingScopeKey !== newScopeKey;
  });

  // Add the new token
  return [
    ...filteredTokens,
    {
      expiresAt: newToken.expiresAt,
      scopes: newToken.scopes,
      singleUse: newToken.singleUse,
      token: newToken.token,
    },
  ];
};
